import React from 'react';
import { FSIOption } from '@/services/fsiReport';
import { Check, AlertCircle, DollarSign, Shield } from 'lucide-react';

interface FSIUtilizationSelectorProps {
  options: FSIOption[];
  zoneFsiConfig: {
    base_fsi: number;
    max_paid_fsi: number;
    total_max_fsi: number;
    tdr_allowed: boolean;
    tdr_max_additional: number;
  };
  selected: string;
  onChange: (value: string) => void;
}

const FSIUtilizationSelector: React.FC<FSIUtilizationSelectorProps> = ({
  options,
  zoneFsiConfig,
  selected,
  onChange,
}) => {
  const getCostBadgeColor = (multiplier: number) => {
    if (multiplier === 1.0) return 'bg-green-100 text-green-800';
    if (multiplier <= 1.15) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="mb-8">
      <h3 className="text-xl font-bold text-gray-800 mb-2">Step 1: Select FSI Utilization Strategy</h3>
      <p className="text-gray-600 mb-4">
        Choose how you want to utilize the Floor Space Index for your development
      </p>
      
      {/* Zone FSI Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-gray-600">Base FSI:</span>
            <span className="font-bold text-gray-800 ml-2">{zoneFsiConfig.base_fsi}</span>
          </div>
          <div>
            <span className="text-gray-600">Max Paid FSI:</span>
            <span className="font-bold text-gray-800 ml-2">{zoneFsiConfig.max_paid_fsi}</span>
          </div>
          <div>
            <span className="text-gray-600">Total Max FSI:</span>
            <span className="font-bold text-gray-800 ml-2">{zoneFsiConfig.total_max_fsi}</span>
          </div>
          <div>
            <span className="text-gray-600">TDR Allowed:</span>
            <span className={`font-bold ml-2 ${zoneFsiConfig.tdr_allowed ? 'text-green-600' : 'text-red-600'}`}>
              {zoneFsiConfig.tdr_allowed ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {options.map((option) => (
          <div
            key={option.id}
            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selected === option.id
                ? 'border-green-600 bg-green-50 shadow-md'
                : 'border-gray-200 bg-white hover:border-green-300 hover:shadow'
            }`}
            onClick={() => onChange(option.id)}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                selected === option.id
                  ? 'border-green-600 bg-green-600'
                  : 'border-gray-300 bg-white'
              }`}>
                {selected === option.id && <Check className="w-3 h-3 text-white" />}
              </div>
              
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-800 mb-1">{option.name}</h4>
                <p className="text-sm text-gray-600 mb-3">{option.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCostBadgeColor(option.cost_multiplier)}`}>
                    <DollarSign className="w-3 h-3 inline mr-1" />
                    Cost: {option.cost_multiplier}x
                  </span>
                  {option.requires_approval && (
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-100 text-orange-800">
                      <Shield className="w-3 h-3 inline mr-1" />
                      Requires Approval
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {selected === option.id && (
              <div className="absolute top-3 right-3">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FSIUtilizationSelector;
